import { useMemo } from 'react';
import { View } from 'ol';
import { usePointToProjection } from '@/utils/map/usePointToProjection';
import { MapInstance, Point } from '@/types/map';

/**
 * Builds a stationary {@link View} centered on a single element, at the exact
 * same resolution the primary map uses for its own maximum zoom (same detail
 * level as the magnifier loupe).
 *
 * Deliberately NOT built via `getMapViewConfig` (zoom/extent/resolutions
 * derived independently): this view never zooms or pans (no interactions are
 * attached), so there's nothing to gain from replicating the primary map's
 * zoom-level/extent constraints, and every constraint is another way for this
 * view's resolution to drift from the primary's — which breaks glyph images,
 * since their on-screen scale is computed as `minResolution / resolution`
 * (see `Glyph.ts`) and assumes `resolution` exactly equals the primary map's
 * own max-zoom resolution. Reading that resolution directly off the primary
 * view and setting it here with no other constraints guarantees an exact
 * match.
 */
export const useOlElementPreviewView = (
  center: Point | undefined,
  mapInstance: MapInstance,
): View | undefined => {
  const pointToProjection = usePointToProjection();

  const projectedCenter = useMemo(
    () => (center ? pointToProjection(center) : undefined),
    [center, pointToProjection],
  );

  return useMemo(() => {
    if (!projectedCenter || !mapInstance) {
      return undefined;
    }
    const primaryView = mapInstance.getView();
    const maxZoomResolution = primaryView.getResolutionForZoom(primaryView.get('originalMaxZoom'));
    return new View({
      center: projectedCenter,
      resolution: maxZoomResolution,
      projection: primaryView.getProjection(),
    });
  }, [projectedCenter, mapInstance]);
};
